/**
 * /settings/activity — workspace audit-log viewer (owner/admin only).
 *
 * Shows the most-recent N entries from `audit_log` filtered to events
 * tagged with this workspace's id in metadata. "Older" pagination
 * pages by created_at via the `before` query param.
 */
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";

interface AuditEntry {
  id: string;
  event: string;
  ip: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  actorEmail: string | null;
}

const PAGE_SIZE = 50;

export const Route = createFileRoute("/_app/settings/activity")({
  component: ActivityPage,
});

function ActivityPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    void load(null);
  }, []);

  async function load(before: string | null) {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { limit: String(PAGE_SIZE) };
      if (before) params.before = before;
      const r = await api.get<{ entries: AuditEntry[] }>("/workspaces/me/audit", {
        params,
      });
      const got = r.data.entries;
      setEntries((cur) => (before ? [...cur, ...got] : got));
      setHasMore(got.length === PAGE_SIZE);
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403) {
        setError("Owner or admin role required to view the audit log.");
      } else {
        setError("Failed to load activity.");
      }
    } finally {
      setLoading(false);
    }
  }

  function loadMore() {
    const last = entries[entries.length - 1];
    if (!last) return;
    void load(last.createdAt);
  }

  return (
    <section style={{ maxWidth: 880, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 8px", fontSize: 28 }}>Activity</h1>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Workspace-scoped audit trail — every privileged action (rename,
        delete, member changes, role checks) writes a row here. Rows
        are append-only and retained indefinitely.
      </p>

      {error && (
        <div
          className="brut-card"
          style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}
        >
          {error}
        </div>
      )}

      {loading && entries.length === 0 ? (
        <p>Loading…</p>
      ) : entries.length === 0 && !error ? (
        <div className="brut-card">
          <p style={{ margin: 0 }}>
            No activity yet. Privileged actions will appear here as they happen.
          </p>
        </div>
      ) : (
        <>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "grid",
              gap: 8,
            }}
          >
            {entries.map((e) => (
              <li
                key={e.id}
                className="brut-card"
                style={{ padding: 12, display: "grid", gap: 4 }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 8,
                    alignItems: "baseline",
                  }}
                >
                  <code
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      background: "var(--accent)",
                      padding: "2px 6px",
                      border: "var(--border)",
                    }}
                  >
                    {e.event}
                  </code>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>
                    {new Date(e.createdAt).toLocaleString()}
                  </span>
                </div>
                <div style={{ fontSize: 13 }}>
                  by{" "}
                  <strong>{e.actorEmail ?? "system / former teammate"}</strong>
                  {e.ip && (
                    <span style={{ color: "var(--muted)" }}> · {e.ip}</span>
                  )}
                </div>
                {e.metadata && Object.keys(e.metadata).length > 0 && (
                  <details>
                    <summary
                      style={{
                        fontSize: 12,
                        color: "var(--muted)",
                        cursor: "pointer",
                      }}
                    >
                      metadata
                    </summary>
                    <pre
                      style={{
                        fontSize: 11,
                        marginTop: 6,
                        marginBottom: 0,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        background: "var(--bg-soft, #f5f5f0)",
                        padding: 8,
                        border: "var(--border)",
                      }}
                    >
                      {JSON.stringify(e.metadata, null, 2)}
                    </pre>
                  </details>
                )}
              </li>
            ))}
          </ul>
          {hasMore && (
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <button
                type="button"
                onClick={loadMore}
                disabled={loading}
                className="brut-button is-secondary"
              >
                {loading ? "Loading…" : "Load older"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
